import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { api } from "@/lib/api/api";
import { QUERY_KEYS } from "@/lib/hooks/queryKeys";
import { useListSecrets } from "@/lib/hooks/useSecrets";
import { cn } from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Trash,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";

interface EnvVarPopoverProps {
  chatId: string;
}

interface SecretFormData {
  secrets: Array<{
    key: string;
    value: string;
    isExisting?: boolean;
  }>;
}

export function EnvVarPopover({ chatId }: EnvVarPopoverProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [secretToDelete, setSecretToDelete] = useState<{
    index: number;
    key: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<Record<number, boolean>>({});
  const [isEditing, setIsEditing] = useState<Record<number, boolean>>({});
  const [isSecretVisible, setIsSecretVisible] = useState<
    Record<number, boolean>
  >({});
  const [operationError, setOperationError] = useState<string | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const initialLoadRef = useRef(true);

  const showToast = {
    secretAdded: (key: string) => {
      toast({
        title: "Secret added",
        description: `"${key}" has been saved successfully`,
        duration: 2000,
      });
    },
    secretUpdated: (key: string) => {
      toast({
        title: "Secret updated",
        description: `"${key}" has been updated successfully`,
        duration: 2000,
      });
    },
    secretDeleted: (key: string) => {
      toast({
        title: "Secret deleted",
        description: `"${key}" has been removed successfully`,
        duration: 2000,
      });
    },
    error: (title: string, description: string) => {
      toast({
        variant: "destructive",
        title,
        description,
        duration: 5000,
      });
    },
  };

  const {
    data: secretsResponse,
    isLoading: isLoadingSecrets,
    error,
  } = useListSecrets(chatId);
  const existingSecretKeys = secretsResponse?.data.secretKeys || [];

  const { control, watch, setValue, reset, register, setFocus, getValues } =
    useForm<SecretFormData>({
      defaultValues: {
        secrets: [],
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "secrets",
  });

  const watchedSecrets = watch("secrets");

  // Use a more controlled useEffect that only depends on server data
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally omit watchedSecrets to prevent infinite loops
  useEffect(() => {
    if (!existingSecretKeys.length) {
      return;
    }

    const currentSecrets = watchedSecrets || [];
    const currentKeys = currentSecrets.map((s) => s.key).filter(Boolean);

    // Only sync if this is initial load or keys actually changed
    const hasKeysChanged =
      existingSecretKeys.length !== currentKeys.length ||
      !existingSecretKeys.every((key) => currentKeys.includes(key));

    if (initialLoadRef.current || hasKeysChanged) {
      const secretsData = existingSecretKeys.map((key) => ({
        key,
        value: "",
        isExisting: true,
      }));

      // Preserve any new secrets being added
      const newSecrets = currentSecrets.filter((secret) => !secret.isExisting);

      reset({ secrets: [...secretsData, ...newSecrets] });
      initialLoadRef.current = false;
    }
  }, [existingSecretKeys, reset]);

  const validateSecretKey = (key: string, index: number) => {
    if (!key.trim()) {
      return "Secret key is required";
    }

    if (!/^[A-Z_][A-Z0-9_]*$/i.test(key)) {
      return "Secret key must start with a letter or underscore and contain only letters, numbers, and underscores";
    }

    const isDuplicate = watchedSecrets.some(
      (secret, i) => i !== index && secret.key === key,
    );
    if (isDuplicate) {
      return "Secret key already exists";
    }

    return null;
  };

  const validateSecretValue = (value: string) => {
    if (!value.trim()) {
      return "Secret value is required";
    }
    return null;
  };

  const startEditing = (index: number) => {
    setIsEditing((prev) => ({ ...prev, [index]: true }));
    setValue(`secrets.${index}.value`, "");
    setTimeout(() => {
      setFocus(`secrets.${index}.value`);
    }, 0);
  };

  const stopEditing = (index: number) => {
    setIsEditing((prev) => ({ ...prev, [index]: false }));
    setValue(`secrets.${index}.value`, "");
  };

  const toggleSecretVisibility = (index: number) => {
    setIsSecretVisible((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const canSaveSecret = (index: number) => {
    const secret = watchedSecrets[index];
    if (!secret) {
      return false;
    }

    const keyError = validateSecretKey(secret.key, index);
    const valueError = validateSecretValue(secret.value);

    return !keyError && !valueError && secret.key.trim() && secret.value.trim();
  };

  const saveSecret = async (index: number) => {
    const secret = watchedSecrets[index];
    if (!secret) {
      return;
    }

    // Only proceed if validation passes
    if (!canSaveSecret(index)) {
      return;
    }

    setIsSubmitting((prev) => ({ ...prev, [index]: true }));
    setOperationError(null);

    try {
      const success = await api.setSecret(chatId, secret.key, secret.value);

      if (success) {
        const isNewSecret = !secret.isExisting;
        if (isNewSecret) {
          showToast.secretAdded(secret.key);
        } else {
          showToast.secretUpdated(secret.key);
        }

        // Invalidate the secrets cache to trigger refetch
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SECRETS, chatId],
        });

        stopEditing(index);
      } else {
        setOperationError("Failed to save secret. Please try again.");
        showToast.error("Failed to save secret", "Please try again.");
      }
    } catch (error) {
      console.error("Error saving secret:", error);
      setOperationError(
        "An error occurred while saving the secret. Please try again.",
      );
      showToast.error("Error saving secret", "An unexpected error occurred.");
    } finally {
      setIsSubmitting((prev) => ({ ...prev, [index]: false }));
    }
  };

  const deleteSecret = async (index: number, key: string) => {
    setIsSubmitting((prev) => ({ ...prev, [index]: true }));
    setOperationError(null);

    try {
      const success = await api.deleteSecret(chatId, key);

      if (success) {
        // Invalidate the secrets cache first to trigger refetch
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.SECRETS, chatId],
        });

        remove(index);
        showToast.secretDeleted(key);

        // Clear all state - it will be rebuilt when the component re-renders with fresh data
        setIsEditing({});
        setIsSubmitting({});
        setIsSecretVisible({});
      } else {
        setOperationError(
          `Failed to delete secret "${key}". Please try again.`,
        );
        showToast.error("Failed to delete secret", "Please try again.");
      }
    } catch (error) {
      console.error("Error deleting secret:", error);
      setOperationError(
        `An error occurred while deleting secret "${key}". Please try again.`,
      );
      showToast.error("Error deleting secret", "An unexpected error occurred.");
    } finally {
      setIsSubmitting((prev) => ({ ...prev, [index]: false }));
      setDeleteDialogOpen(false);
      setSecretToDelete(null);
    }
  };

  const addNewSecret = () => {
    // Check if the last field has an empty key
    if (fields.length > 0) {
      const lastSecret = watchedSecrets[fields.length - 1];
      if (!lastSecret?.key?.trim()) {
        return;
      }
    }

    const newIndex = fields.length;
    append({ key: "", value: "" });
    setIsEditing((prev) => ({ ...prev, [newIndex]: true }));

    // Focus the key input after a brief delay to ensure it's rendered
    setTimeout(() => {
      setFocus(`secrets.${newIndex}.key`);
    }, 10);
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      // Only save if both key and value are present
      if (canSaveSecret(index)) {
        saveSecret(index);
      }
    }
    if (e.key === "Escape") {
      e.preventDefault();
      const secret = watchedSecrets[index];
      const isNewSecret = !secret?.isExisting;
      if (isNewSecret && !secret?.key && !secret?.value) {
        remove(index);
      } else {
        stopEditing(index);
      }
    }
  };

  const handleBlur = (index: number) => {
    const secret = watchedSecrets[index];
    const isSecretEditing = isEditing[index];
    const isNewSecret = !secret?.isExisting;

    if (isSecretEditing && canSaveSecret(index)) {
      saveSecret(index);
    } else if (isNewSecret && !secret?.key && !secret?.value) {
      remove(index);
    } else {
      stopEditing(index);
    }
  };

  const handlePopoverOpenChange = (open: boolean) => {
    setIsPopoverOpen(open);

    // Clean up partially filled secrets when closing
    if (!open) {
      const currentSecrets = getValues("secrets");
      const secretsToRemove: number[] = [];

      currentSecrets.forEach((secret, index) => {
        // Use isExisting flag to determine if this is a new secret
        const isNewSecret = !secret.isExisting;
        // Remove new secrets that are invalid
        if (
          isNewSecret &&
          ((!secret?.key && !secret?.value) || // Completely empty secret
            (secret?.key && !secret?.value) || // Secret with key but no value
            (secret?.value && !secret?.key)) // Secret with value but no key
        ) {
          secretsToRemove.push(index);
        }
      });

      // Remove in reverse order to maintain correct indices
      for (const index of secretsToRemove.reverse()) {
        remove(index);
      }

      // Clear editing states
      setIsEditing({});
      setIsSecretVisible({});
    }
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="icon"
          className="h-7 w-7"
          aria-label="Add Environment Variable"
        >
          <KeyRound className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[396px] p-0 border-0"
        align="end"
        side="bottom"
      >
        <div className="">
          <div className="px-1">
            <h4 className="h-8 p-2 font-4 leading-5">
              Add Environment Variables
            </h4>
          </div>
          <hr className="my-1" />
          <div className="py-3">
            <div className="flex flex-col gap-4 px-3">
              <p className="text-xs leading-4">
                Environment variables will be encrypted and stored securely on
                our servers.
              </p>

              {/* Error display */}
              {(error || operationError) && (
                <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded text-red-700">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span className="text-xs">
                    {operationError ||
                      (error instanceof Error
                        ? error.message
                        : "Failed to load secrets")}
                  </span>
                </div>
              )}

              {isLoadingSecrets ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="ml-2 text-sm text-muted-foreground">
                    Loading secrets...
                  </span>
                </div>
              ) : (
                <>
                  {fields.map((field, index) => {
                    const secret = watchedSecrets[index];
                    const isSecretEditing = isEditing[index];
                    const isNewSecret = !secret.isExisting;
                    const keyError = secret?.key
                      ? validateSecretKey(secret.key, index)
                      : null;
                    const valueError = secret?.value
                      ? validateSecretValue(secret.value)
                      : null;

                    return (
                      <div key={field.id} className="flex gap-2">
                        <div className="flex gap-2 flex-1">
                          <div className="w-1/2">
                            <Input
                              {...register(`secrets.${index}.key`)}
                              placeholder="API_KEY"
                              value={secret?.key || ""}
                              onChange={(e) =>
                                setValue(`secrets.${index}.key`, e.target.value)
                              }
                              onKeyDown={(e) => handleKeyDown(e, index)}
                              className={cn(
                                "h-8 py-2 px-3 font-mono",
                                keyError && "border-red-500",
                                !isNewSecret && "bg-muted",
                              )}
                              readOnly={!isNewSecret}
                              disabled={isSubmitting[index]}
                            />
                          </div>
                          <div className="w-1/2 relative">
                            <Input
                              {...register(`secrets.${index}.value`)}
                              placeholder={
                                isSecretEditing ? "Enter new value" : "••••••••"
                              }
                              value={
                                isNewSecret
                                  ? secret?.value || ""
                                  : isSecretEditing
                                    ? secret?.value || ""
                                    : "••••••••"
                              }
                              onChange={(e) =>
                                setValue(
                                  `secrets.${index}.value`,
                                  e.target.value,
                                )
                              }
                              onFocus={() => {
                                if (!isNewSecret) {
                                  startEditing(index);
                                }
                              }}
                              onKeyDown={(e) => handleKeyDown(e, index)}
                              onBlur={() => handleBlur(index)}
                              className={cn(
                                "h-8 py-2 px-3 font-mono pr-12",
                                valueError && "border-red-500",
                                !isNewSecret &&
                                  !isSecretEditing &&
                                  "text-muted-foreground",
                                "transition-colors-none",
                              )}
                              type={
                                isNewSecret || isSecretEditing
                                  ? isSecretVisible[index]
                                    ? "text"
                                    : "password"
                                  : "text"
                              }
                              disabled={isSubmitting[index]}
                            />
                            {!isNewSecret && !isSecretEditing && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 px-2 text-xs"
                                onClick={() => startEditing(index)}
                                disabled={isSubmitting[index]}
                              >
                                Edit
                              </Button>
                            )}
                            {!isNewSecret && isSecretEditing && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 px-2"
                                onClick={() => toggleSecretVisibility(index)}
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={isSubmitting[index]}
                                aria-label={
                                  isSecretVisible[index]
                                    ? "Hide secret value"
                                    : "Show secret value"
                                }
                              >
                                {isSecretVisible[index] ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                            {isNewSecret && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-6 px-2"
                                onClick={() => toggleSecretVisibility(index)}
                                onMouseDown={(e) => e.preventDefault()}
                                disabled={isSubmitting[index]}
                                aria-label={
                                  isSecretVisible[index]
                                    ? "Hide secret value"
                                    : "Show secret value"
                                }
                              >
                                {isSecretVisible[index] ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="w-12 flex justify-center">
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => {
                              if (isNewSecret) {
                                // For new secrets, just remove from array
                                remove(index);
                              } else {
                                // For existing secrets, show confirmation dialog
                                setSecretToDelete({
                                  index,
                                  key: secret?.key || "",
                                });
                                setDeleteDialogOpen(true);
                              }
                            }}
                            aria-label="Delete secret"
                            disabled={isSubmitting[index]}
                          >
                            {isSubmitting[index] ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          <div className="pt-0 p-3">
            <Button
              type="button"
              onClick={addNewSecret}
              variant="secondary"
              className="inline-flex h-7 py-0 px-3"
              disabled={isLoadingSecrets}
            >
              + Add Variable
            </Button>
          </div>
        </div>
      </PopoverContent>

      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) {
            setSecretToDelete(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Secret</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the secret "{secretToDelete?.key}
              "? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeleteDialogOpen(false);
                setSecretToDelete(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (secretToDelete) {
                  deleteSecret(secretToDelete.index, secretToDelete.key);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Popover>
  );
}
